import { FormattedMessage } from 'react-intl';
import messages from './messages';
import { YES_NO_OPTIONS } from '../../constants/selectOptions';
import { textingColumnTableFilterFn } from '../../utils/texting';
import { TEXTING_FILTER_OPTIONS } from '../../constants/texting';
import { preferencesColumnTableFilterFn } from 'containers/ManageAffordableWaitlist/utils';

const PREFERENCES_FILTER_OPTIONS_V1 = [
  {
    text: 'Vawa',
    value: 'vawa',
  },
  {
    text: 'Elderly',
    value: 'elderly',
  },
  {
    text: 'Veteran',
    value: 'veteran',
  },
  {
    text: 'Disabled',
    value: 'disabled',
  },
  {
    text: 'Working Family',
    value: 'workingFamily',
  },
];

const PREFERENCES_FILTER_OPTIONS_V2 = [
  {
    text: 'Veteran or the unmarried spouse of a service person killed while in active service',
    value: 'veteran',
  },
  {
    text: 'Young adult transitioning out of foster care',
    value: 'fostercare',
  },
  {
    text: 'Being involuntarily displaced',
    value: 'involuntarilydisplaced',
  },
  { text: 'Disability', value: 'disability' },
  { text: 'Elderly', value: 'elderly' },
  { text: 'Homeless', value: 'homeless' },
  {
    text: 'Paying more than 50% of your income for rent',
    value: 'rentburden',
  },
  { text: 'Residency', value: 'residency' },
  { text: 'Victim', value: 'victim' },
  { text: 'Working families', value: 'workingfamilies' },
];

export const HEADERS = (waitlistV2Flag = false) => [
  {
    id: 'position',
    label: <FormattedMessage {...messages.position} />,
    dataType: 'number',
    sortable: true,
  },
  {
    id: 'affordablePrograms',
    label: <FormattedMessage {...messages.affordablePrograms} />,
    dataType: 'string',
    multiselect: true,
  },
  {
    id: 'applicationCode',
    label: <FormattedMessage {...messages.applicationCode} />,
    dataType: 'number',
    searchable: true,
  },
  {
    id: 'applicants',
    label: <FormattedMessage {...messages.applicants} />,
    dataType: 'string',
    searchable: true,
  },
  {
    id: 'texting',
    label: <FormattedMessage {...messages.texting} />,
    dataType: 'number',
    multiselect: true,
    filterOptions: TEXTING_FILTER_OPTIONS,
  },
  {
    id: 'householdStatus',
    label: <FormattedMessage {...messages.householdStatus} />,
    dataType: 'string',
    multiselect: true,
  },
  {
    id: 'status',
    label: <FormattedMessage {...messages.status} />,
    dataType: 'string',
    multiselect: true,
  },
  {
    id: 'phoneNumber',
    label: <FormattedMessage {...messages.phoneNumber} />,
    dataType: 'string',
    searchable: true,
  },
  {
    id: 'emailAddress',
    label: <FormattedMessage {...messages.emailAddress} />,
    dataType: 'string',
    searchable: true,
  },
  {
    id: 'desiredMoveInDate',
    label: <FormattedMessage {...messages.desiredMoveInDate} />,
    dataType: 'date',
    searchable: true,
  },
  {
    id: 'applicationReceived',
    label: <FormattedMessage {...messages.applicationReceived} />,
    dataType: 'date',
    searchable: true,
  },
  {
    id: 'applicationRemoved',
    label: <FormattedMessage {...messages.applicationRemoved} />,
    dataType: 'date',
    searchable: true,
  },
  {
    id: 'preferredUnitSize',
    label: <FormattedMessage {...messages.preferredUnitSize} />,
    dataType: 'string',
    multiselect: true,
  },
  {
    id: 'householdSize',
    label: <FormattedMessage {...messages.householdSize} />,
    dataType: 'number',
    searchable: true,
  },
  {
    id: 'incomeLevel',
    label: <FormattedMessage {...messages.incomeLevel} />,
    dataType: 'string',
    multiselect: true,
  },
  {
    id: 'datesContacted',
    label: <FormattedMessage {...messages.datesContacted} />,
    dataType: 'date',
    searchable: true,
  },
  {
    id: 'moveInDate',
    label: <FormattedMessage {...messages.moveInDate} />,
    dataType: 'date',
    searchable: true,
  },
  {
    id: 'needsUnitAccessible',
    label: <FormattedMessage {...messages.needsUnitAccessible} />,
    sortable: true,
    searchable: false,
    multiselect: true,
    dataType: 'boolean',
    filterOptions: YES_NO_OPTIONS,
  },
  {
    id: 'requestingAccommodation',
    label: <FormattedMessage {...messages.requestingAccommodation} />,
    dataType: 'boolean',
    multiselect: true,
    filterOptions: YES_NO_OPTIONS,
  },
  {
    id: 'preferences',
    label: <FormattedMessage {...messages.preferences} />,
    dataType: 'string',
    searchable: false,
    multiselect: true,
    filterOptions: waitlistV2Flag
      ? PREFERENCES_FILTER_OPTIONS_V2
      : PREFERENCES_FILTER_OPTIONS_V1,
  },
  {
    id: 'assignedTo',
    label: <FormattedMessage {...messages.assignedTo} />,
    dataType: 'string',
    searchable: true,
  },
  {
    id: 'notes',
    dataType: 'string',
    label: <FormattedMessage {...messages.notes} />,
  },
  {
    id: 'actions',
    label: <FormattedMessage {...messages.actions} />,
    dataType: 'string',
  },
];

export const PROPERTY_PATH_MAP = {
  position: {
    path: 'position',
    dataType: 'number',
  },
  affordablePrograms: {
    path: 'affordablePrograms',
    dataType: 'string',
  },
  applicationCode: {
    path: 'applicationCode',
    dataType: 'number',
  },
  applicants: {
    path: 'applicants',
    dataType: 'string',
  },
  texting: {
    path: 'texting.TOTAL',
    dataType: 'number',
    filterPath: 'texting',
    filterFn: textingColumnTableFilterFn,
  },
  householdStatus: {
    path: 'householdStatus',
    dataType: 'string',
  },
  status: {
    path: 'applicationStatus.name',
    dataType: 'string',
  },
  phoneNumber: {
    path: 'phoneNumber',
    dataType: 'string',
  },
  emailAddress: {
    path: 'emailAddress',
    dataType: 'string',
  },
  desiredMoveInDate: {
    path: 'desiredMoveInStartDate',
    dataType: 'date',
  },
  applicationReceived: {
    path: 'applicationReceived',
    dataType: 'date',
  },
  applicationRemoved: {
    path: 'applicationRemoved',
    dataType: 'date',
  },
  preferredUnitSize: {
    path: 'preferredUnitSize',
    dataType: 'number',
  },
  householdSize: {
    path: 'householdSize',
    dataType: 'number',
  },
  incomeLevel: {
    path: 'incomeLevel',
    dataType: 'string',
  },
  datesContacted: {
    path: 'datesContacted',
    dataType: 'date',
  },
  moveInDate: {
    path: 'moveInDate',
    dataType: 'date',
  },
  needsUnitAccessible: {
    path: 'needsUnitAccessible',
    dataType: 'boolean',
  },
  requestingAccommodation: {
    path: 'requestingAccommodation',
    dataType: 'boolean',
  },
  preferences: {
    path: 'preferences',
    dataType: 'string',
    filterPath: 'preferences',
    filterFn: preferencesColumnTableFilterFn,
  },
  assignedTo: {
    path: 'assigneeName',
    dataType: 'string',
  },
};
